import type { StrategyEditorSection, StrategyEditorSectionId } from '../strategyEditorSections';

interface StrategySectionNavProps {
    sections: StrategyEditorSection[];
    activeSectionId: StrategyEditorSectionId;
    /** Number of unsaved changes per section; drives the amber dot. */
    changeCountsBySection: Partial<Record<StrategyEditorSectionId, number>>;
    /** Short right-aligned annotation per section, e.g. the attached rule version or leg count. */
    metaBySection: Partial<Record<StrategyEditorSectionId, string>>;
    /** Number of blocking issues surfaced on Overview; drives the red dot. */
    attentionCount: number;
    onSelect: (sectionId: StrategyEditorSectionId) => void;
}

/**
 * Primary navigation for the strategy editor. Vertical on desktop so seven sections fit without
 * wrapping and the old right-hand summary rail's width can go back to the content; a horizontally
 * scrolling strip below `lg`. Each item carries its own state so unsaved edits are never hidden
 * behind an inactive section.
 */
const StrategySectionNav = ({
    sections,
    activeSectionId,
    changeCountsBySection,
    metaBySection,
    attentionCount,
    onSelect
}: StrategySectionNavProps) => (
    <nav
        aria-label="Strategy sections"
        className="flex gap-1 overflow-x-auto rounded-lg border border-zinc-800 bg-[#161616] p-2 lg:sticky lg:top-24 lg:flex-col lg:overflow-visible"
    >
        {sections.map((section) => {
            const isActive = section.id === activeSectionId;
            const changeCount = changeCountsBySection[section.id] ?? 0;
            const meta = metaBySection[section.id];
            const showsAttentionDot = section.id === 'overview' && attentionCount > 0;

            return (
                <button
                    aria-current={isActive}
                    className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors lg:w-full lg:shrink ${
                        isActive ? 'bg-p314-blue/15 font-medium text-sky-300' : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-white'
                    }`}
                    key={section.id}
                    type="button"
                    onClick={() => onSelect(section.id)}
                >
                    <span className="flex-1 whitespace-nowrap">{section.label}</span>
                    {meta !== undefined && <span className="font-mono text-[11px] tabular-nums text-zinc-500">{meta}</span>}
                    {changeCount > 0 && (
                        <span
                            aria-label={`${changeCount} unsaved ${changeCount === 1 ? 'change' : 'changes'}`}
                            className="size-1.5 shrink-0 rounded-full bg-yellow-400"
                            title={`${changeCount} unsaved ${changeCount === 1 ? 'change' : 'changes'}`}
                        />
                    )}
                    {showsAttentionDot && changeCount === 0 && (
                        <span
                            aria-label={`${attentionCount} ${attentionCount === 1 ? 'issue' : 'issues'} need attention`}
                            className="size-1.5 shrink-0 rounded-full bg-red-400"
                            title={`${attentionCount} ${attentionCount === 1 ? 'issue' : 'issues'} need attention`}
                        />
                    )}
                </button>
            );
        })}
    </nav>
);

export default StrategySectionNav;
